import React, { useState } from 'react';
import {
  Toolbar, Typography, Box, Container, IconButton,
  Drawer, List, ListItem, ListItemButton, ListItemText, Divider, CircularProgress,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUser, logout, getLogoUrl } from '../services/api';
import { useSchool } from '../contexts/SchoolContext';

export default function Layout({ children }) {
  const user = getUser();
  const { school, loading: schoolLoading } = useSchool();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const role = user?.role;
  const navItems = [
    ...(role === 'admin' ? [
      { label: 'Schools', path: '/admin#schools' },
      { label: 'Teachers', path: '/admin#teachers' },
      { label: 'Students', path: '/admin#students' },
      { label: 'Curriculums', path: '/admin#curriculums' },
    ] : []),
    ...(role === 'teacher' ? [
      { label: 'Students', path: '/teacher#students' },
      { label: 'Grades', path: '/teacher#grades' },
      { label: 'Curriculums', path: '/teacher#curriculums' },
    ] : []),
    ...(role === 'student' ? [
      { label: 'Curriculums', path: '/student' },
    ] : []),
    { label: 'FAQ', path: '/faq' },
    { label: 'Support', path: '/support' },
  ];

  const handleNav = (path) => {
    // For hash-only changes on the same page, update hash directly so React Router detects it
    if (path.includes('#') && pathname === path.split('#')[0]) {
      window.location.hash = path.split('#')[1];
    } else {
      nav(path);
    }
    setDrawerOpen(false);
  };

  const logoSrc = school?.logoKey ? getLogoUrl(school.logoKey) : null;
  const showSchoolName = (role === 'teacher' || role === 'student') && school && !logoSrc;
  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName.charAt(0).toUpperCase()} ${user.lastName}` 
    : user?.email || 'User';
  const roleLabel = role === 'school_admin' ? 'School Admin' : role ? role.charAt(0).toUpperCase() + role.slice(1) : '';

  const renderLogo = () => {
    if (schoolLoading) return <CircularProgress size={36} sx={{ my: 2 }} />;
    if (showSchoolName) {
      return <Typography variant="h5" sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1, py: 2 }}>{school.name}</Typography>;
    }
    return <img src={logoSrc || '/assets/logo-light.png'} alt={school?.name || 'Soapbox'} style={{ height: 90, marginRight: 10, maxWidth: 320, objectFit: 'contain', padding: '12px 0' }} />;
  };

  return (
    <Box>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ backgroundColor: '#333', borderRadius: { xs: 0, sm: 1 }, mt: { xs: 0, sm: 2 }, px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center' }}>
          <Toolbar disableGutters sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexGrow: 1 }} onClick={() => nav('/')}>
              {renderLogo()}
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', mr: 2 }}>
              <Typography variant="body2">{displayName}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{roleLabel}</Typography>
            </Box>
            <IconButton color="inherit" onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>
          </Toolbar>
        </Box>
      </Container>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
            <Typography variant="caption" color="text.secondary" textTransform="capitalize">{user?.role === 'school_admin' ? 'School Admin' : user?.role}</Typography>
          </Box>
          <Divider />
          <List>
            {navItems.map(item => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton selected={pathname === item.path || (item.path === '/' && ['/', '/admin', '/teacher', '/student'].includes(pathname))}
                  onClick={() => handleNav(item.path)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNav('/change-password')}>
                <ListItemText primary="Change Password" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); logout(); }}>
                <ListItemText primary="Logout" sx={{ color: 'error.main' }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Container maxWidth="lg" sx={{ mt: 3, mb: 10, px: { xs: 2, sm: 3 } }}>{children}</Container>

      <Box component="footer" sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, py: 1, px: 2,
        backgroundColor: '#2a2a2a', borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, zIndex: 1200,
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Powered by Soapbox LLC</Typography>
        <img src="/assets/icon-footer.png" alt="Soapbox" style={{ height: 36 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
          © {new Date().getFullYear()} All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
